import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { T } from '@transifex/react';
import { tx } from '@transifex/native';
import { toast } from 'sonner';
import { create } from 'zustand';
import { PropertyProvider } from 'src/context/property-context';
import { LoginShell } from 'src/layouts/LoginShell';
import { AddressSelect } from 'src/components/login/AddressSelect';
import { apiClient } from 'src/api/client';
import { getAuthPropertyId, isDevBypassAuth } from 'src/api/state';
import { useQuery } from '@tanstack/react-query';
import { CriptioLogin } from 'src/components/CriptioLogin';
import { Button } from '@/components/ui/button';
import { useCompany } from './Company.store';
import { LoadingPage } from '../components/shared';
import { useUser } from './User.store';
// import { useMitId } from './useMitId';

export type TValue = {
  id: string;
  type: {
    id: string;
    name: string;
  };
  location: {
    latitude: number;
    longitude: number;
    name: string;
  };
  ticket_types: [
    {
      id: string;
      name: string;
      service: {
        id: string;
        name: string;
        service_type: string;
      };
      is_container_level: true;
      is_property_level: true;
      portal_require_contact_information: true;
      portal_description: string;
      portal_date_label: string;
      portal_date_help_text: string;
      portal_date_required: true;
      portal_date_hidden: true;
      portal_note_label: string;
      portal_note_help_text: string;
      portal_note_required: true;
      portal_note_hidden: true;
      portal_price_show_zero: true;
      portal_contact_information_name_required: true;
      portal_contact_information_phone_number_required: true;
      portal_contact_information_email_required: true;
      portal_contact_information_next_button_label: string;
      portal_confirm_title: string;
      portal_confirm_subtitle: string;
      portal_service_type_bulk_waste_limit: number;
      portal_service_type_bulk_waste_limit_label: string;
      portal_service_type_bulk_waste_limit_help_text: string;
      portal_dropdown_hidden: true;
      portal_dropdown_required: true;
      portal_dropdown_label: string;
      portal_dropdown_help_text: string;
      portal_dropdown_options: [string];
    },
  ];
};

type TStore = {
  data: TValue;
  computed: {
    ticketTypesMap: Record<string, TValue['ticket_types'][0]>;
  };
};

const usePropertyStore = create<TStore>()(() => ({}) as TStore);

export const useProperty = () => {
  return usePropertyStore();
};

/*
1. Get propertyId

- get property id from the localStorage. if not set then go to the property selection page.
- if we get the id from selection page then set it on the localStorage.

2. Once we have the property id we need to check if anything else is required

- If LOGIN_METHOD_NONE:
  - set property and OK.
- If LOGIN_METHOD_MIDID_POST_SEARCH:
  - set property and OK.
- If LOGIN_METHOD_MIDID_PRE_SEARCH:
  - If CheckIfPropertyPropertyIsAnanymous is true then redirect to mid id.
  - else set proerty and OK.

*/

const persistSelection = (id: string) => {
  localStorage.setItem('AUTH_PROPERTY_ID', id);
  localStorage.setItem(
    'AUTH_TOKEN_EXPIRY',
    dayjs().add(60, 'minutes').toISOString()
  );
};

// Maps the ensure-bfe failure status to a translatable message. Each branch
// keeps a literal tx.translate('...') call so the Transifex CLI can statically
// extract the source strings — a status->string lookup table would not extract.
const ensureBfeErrorMessage = (status?: number): string => {
  if (status === 422) {
    return tx.translate(
      'Your address is malformed. Please contact your municipality.'
    );
  }
  if (status === 404) {
    return tx.translate(
      'Your address could not be found in the national property registry. Please contact your municipality.'
    );
  }
  return tx.translate(
    'Service temporarily unavailable. Please try again later.'
  );
};

const CitizenPropertyStoreProvider = ({
  children,
}: {
  children: JSX.Element;
}): JSX.Element => {
  const [selectPropertyJsx, setSelectPropertyJsx] =
    useState<JSX.Element | null>(null);
  const [notAllowed, setNotAllowed] = useState(false);
  const { data: company } = useCompany();
  const {
    jsx: jsxCriptioLogin,
    redirectToMitId,
    cancelMitIdSelection,
  } = CriptioLogin.useCriptioLogin();
  const { data: user, logout } = useUser();
  const property = localStorage.getItem('AUTH_PROPERTY_NAME');

  useEffect(() => {
    if (
      (company.login_method === 'user' || company.login_method === 'bankid') &&
      company.login_method_user === 'pre_auth_search' &&
      user.anonymous
    ) {
      localStorage.setItem('AUTH_PROPERTY_ID', '');
    }
  }, [company.login_method, company.login_method_user, user.anonymous]);

  const { isLoading, refetch } = useQuery({
    queryKey: ['PropertyStoreProvider', 'Get'],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      setNotAllowed(false); // reseting the state when get's refetch again
      let propertyId = getAuthPropertyId();

      if (!propertyId) {
        propertyId = await new Promise<string>((res) => {
          const jsx = (
            <div className="flex flex-col items-stretch gap-4">
              <AddressSelect
                shouldShowOptions={
                  company.login_method_user === 'post_auth_search' ||
                  isDevBypassAuth()
                }
                onChange={(v, option) => {
                  localStorage.setItem('AUTH_PROPERTY_NAME', option.label);
                  if (
                    (company.login_method === 'user' ||
                      company.login_method === 'bankid') &&
                    company.login_method_user === 'pre_auth_search' &&
                    user.anonymous
                  ) {
                    if (
                      company.login_method === 'user' &&
                      company.datafordeler_enabled
                    ) {
                      apiClient
                        .get(`property/${v}/ensure-bfe`)
                        .then(() => {
                          persistSelection(v);
                          redirectToMitId();
                        })
                        .catch((err) => {
                          toast.error(
                            ensureBfeErrorMessage(err?.response?.status),
                            { id: 'ensure-bfe-error' }
                          );
                          // Leave the picker mounted so the user can re-select
                          // without a remount/reload; just undo the optimistic
                          // name write from above.
                          localStorage.setItem('AUTH_PROPERTY_ID', '');
                          localStorage.setItem('AUTH_PROPERTY_NAME', '');
                        });
                    } else {
                      persistSelection(v);
                      redirectToMitId();
                    }
                  } else {
                    res(v);
                  }
                }}
              />
              {company.login_method_user === 'post_auth_search' && (
                <Button variant="ghost" onClick={logout}>
                  <T _str="Login with another account" />
                </Button>
              )}
            </div>
          );
          setSelectPropertyJsx(jsx);
        }).then((v) => {
          persistSelection(v as string);
          setSelectPropertyJsx(null);
          return v;
        });
      }

      // if there is no token then fetch it using company login method.
      return apiClient
        .get(`property/${propertyId}`)
        .then((d) => {
          if (d.data.id) {
            const p = d.data as TValue;
            usePropertyStore.setState({
              data: d.data,
              computed: {
                ticketTypesMap: p.ticket_types.reduce(
                  (acc, t) => ({ ...acc, [t.id]: t }),
                  {}
                ),
              },
            });
          }
          return null;
        })
        .catch((e) => {
          if (e.response.status === 404) {
            // wrong property selected. Not authorized.
            setNotAllowed(true);
          }
          return null;
        });
    },
  });

  if (jsxCriptioLogin)
    return (
      <LoginShell title={property}>
        <div className="flex flex-col items-stretch gap-4">
          <Button variant="ghost" onClick={cancelMitIdSelection}>
            <T _str="Change to another address" />
          </Button>
          {jsxCriptioLogin}
        </div>
      </LoginShell>
    );
  if (selectPropertyJsx) return <LoginShell>{selectPropertyJsx}</LoginShell>;

  // Only blank the app for a genuine first load (isLoading). Background
  // refetches keep the current view instead of flashing the full-page spinner.
  // NOTE: the queryKey is static (the property id is read from localStorage
  // inside queryFn), so a property switch is a same-key refetch — NOT isLoading.
  // It's handled earlier by clearing AUTH_PROPERTY_ID, which routes through the
  // selectPropertyJsx guard above before execution reaches here.
  if (isLoading) {
    return <LoadingPage />;
  }

  if (notAllowed) {
    return (
      <LoginShell>
        <div className="flex w-full flex-col items-center gap-5 rounded-lg bg-secondary p-10 text-center">
          <h2 className="text-xl font-semibold">
            <T _str="No Access" />
          </h2>
          <p className="text-base text-muted-foreground">
            <T
              _str="You do not have access to visit this property. Please try another address or login again. Your currently chose: {address}"
              address={<b>{property}</b>}
            />
          </p>
          <Button
            onClick={() => {
              localStorage.setItem('AUTH_PROPERTY_ID', '');
              refetch();
            }}
          >
            <T _str="Change to another address" />
          </Button>
        </div>
        <CriptioLogin
          style={{ margin: 'auto' }}
          onClick={() => {
            localStorage.setItem('AUTH_TOKEN', '');
            localStorage.setItem('AUTH_PROPERTY_ID', '');
          }}
        />
      </LoginShell>
    );
  }

  return <PropertyProvider>{children}</PropertyProvider>;
};

const isHaulerPath = () =>
  typeof window !== 'undefined' &&
  window.location.pathname.startsWith('/hauler');

export const PropertyStoreProvider = ({
  children,
}: {
  children: JSX.Element;
}): JSX.Element => {
  // Haulers aren't tied to a single property the way citizens are. Skip the
  // citizen property-selection gate and pass through to the hauler route tree.
  if (isHaulerPath()) {
    return children;
  }
  return (
    <CitizenPropertyStoreProvider>{children}</CitizenPropertyStoreProvider>
  );
};
